import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormPrice,
  FormRoot,
  FormTextarea,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { UserCogIcon } from "lucide-react";
import { useEffect, useMemo } from "react";
import { z } from "zod";
import {
  useBulkCreateEventOutsourcesFromStaff,
  useBulkCreateOutsourcesFromStaff,
  useEventStaffQuery,
  useOutsourceCategoriesQuery,
} from "../api/queries";
import { OutsourceCategoryField } from "./outsource-form-ui";
import { StaffMultiSelect } from "./StaffMultiSelect";

const bulkFormSchema = z
  .object({
    staffIds: z.array(z.string()).min(1, "ເລືອກຢ່າງໜ້ອຍ 1 Staff"),
    categoryId: z.string().min(1, "ເລືອກຫມວດ"),
    agreedAmount: z.number().int().nonnegative().optional(),
    deliverableSummary: z.string().optional(),
  })
  .superRefine((vals, ctx) => {
    // validated in component by mode
    if (vals.agreedAmount != null && vals.agreedAmount <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["agreedAmount"],
        message: "ຍອດຕ້ອງຫຼາຍກວ່າ 0",
      });
    }
  });

type BulkFormValues = z.infer<typeof bulkFormSchema>;

type OutsourceBulkFromStaffDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "event" | "master";
  eventId?: string;
  existingDisplayNames?: string[];
};

export function OutsourceBulkFromStaffDialog({
  open,
  onOpenChange,
  mode,
  eventId,
  existingDisplayNames = [],
}: OutsourceBulkFromStaffDialogProps) {
  const categoriesQuery = useOutsourceCategoriesQuery(open);
  const categories = categoriesQuery.data?.data ?? [];
  const bulkEvent = useBulkCreateEventOutsourcesFromStaff(eventId ?? "");
  const bulkMaster = useBulkCreateOutsourcesFromStaff();
  const eventStaffQuery = useEventStaffQuery(eventId ?? "", {
    activeOnly: true,
    enabled: open && mode === "event" && !!eventId,
  });

  const methods = RHF.useForm<BulkFormValues>({
    resolver: zodResolver(bulkFormSchema),
    defaultValues: {
      staffIds: [],
      categoryId: "",
      agreedAmount: 0,
      deliverableSummary: "",
    },
  });

  const staffIds = methods.watch("staffIds");
  const submitting = bulkEvent.isPending || bulkMaster.isPending;

  const existingNameSet = useMemo(
    () =>
      new Set(
        existingDisplayNames.map((name) => name.trim().toLocaleLowerCase()),
      ),
    [existingDisplayNames],
  );

  const eventStaffCandidates = useMemo(() => {
    if (mode !== "event") return [];
    return (eventStaffQuery.data ?? []).filter(
      (row) =>
        row.isActive &&
        !existingNameSet.has(row.displayName.trim().toLocaleLowerCase()),
    );
  }, [mode, eventStaffQuery.data, existingNameSet]);

  useEffect(() => {
    if (!open) return;
    methods.reset({
      staffIds: [],
      categoryId: "",
      agreedAmount: 0,
      deliverableSummary: "",
    });
  }, [open, methods]);

  const handleSelectEventStaff = () => {
    const ids = eventStaffCandidates.map((row) => row.staffId);
    methods.setValue("staffIds", ids, { shouldDirty: true, shouldValidate: true });
  };

  const handleSubmit = async (vals: BulkFormValues) => {
    if (mode === "event") {
      if (!eventId) return;
      if (!vals.agreedAmount || vals.agreedAmount <= 0) {
        methods.setError("agreedAmount", {
          message: "ຍອດຕ້ອງຫຼາຍກວ່າ 0",
        });
        return;
      }
      await bulkEvent.mutateAsync({
        staffIds: vals.staffIds,
        categoryId: vals.categoryId,
        agreedAmount: vals.agreedAmount,
        deliverableSummary: vals.deliverableSummary?.trim() || null,
      });
    } else {
      await bulkMaster.mutateAsync({
        staffIds: vals.staffIds,
        categoryId: vals.categoryId,
      });
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] w-[calc(100%-2rem)] max-w-lg overflow-x-hidden overflow-y-auto sm:w-full">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCogIcon className="size-4 text-primary" />
            ດຶງ Staff ຫຼາຍຄົນ
          </DialogTitle>
          <DialogDescription>
            {mode === "event"
              ? "ເພີ່ม Outsource AP ຫຼายแถວພ້ອມກັນ — ຂໍ້ມູນຊື່/ເບີ/ບັນຊີ copy ຈາກ Staff"
              : "ສ້າງ Outsource master ຫຼາຍລາຍການພ້ອມກັນ"}
          </DialogDescription>
        </DialogHeader>

        <FormRoot
          methods={methods}
          onSubmit={handleSubmit}
          className="min-w-0 w-full max-w-full space-y-4"
        >
          <div className="min-w-0 max-w-full space-y-2">
            <RHF.Controller
              name="staffIds"
              control={methods.control}
              render={({ field }) => (
                <StaffMultiSelect
                  required
                  values={field.value}
                  onValuesChange={field.onChange}
                  queryKeyPrefix={[
                    "performance",
                    "staff",
                    "lookup",
                    "outsource-bulk",
                    mode,
                    eventId ?? "master",
                  ]}
                  disabled={submitting}
                  hint="ເລືອກຫຼາຍ Staff ຈາກລາຍຊື່ master"
                />
              )}
            />
            {mode === "event" && eventStaffCandidates.length > 0 ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={submitting || eventStaffQuery.isLoading}
                onClick={handleSelectEventStaff}
              >
                ເລືອກ Staff ในงาน ({eventStaffCandidates.length})
              </Button>
            ) : null}
          </div>

          <div className="min-w-0 max-w-full">
            <OutsourceCategoryField
              name="categoryId"
              categories={categories}
              disabled={submitting}
            />
          </div>

          {mode === "event" ? (
            <div className="min-w-0 max-w-full space-y-4">
              <FormPrice
                name="agreedAmount"
                label="ຍອດຕົກລົງ (ກີບ)"
                hint="ໃຊ້ຄ່າດຽວກັນທຸກແຖວ"
                requiredMark
              />
              <FormTextarea
                name="deliverableSummary"
                label="Deliverable (ທາງເລືອກ)"
                hint="ໃຊ້ຄ່າດຽວກັນທຸກແຖວ"
                rows={2}
              />
            </div>
          ) : null}

          {staffIds.length > 0 ? (
            <div className="rounded-lg border bg-muted/20 p-3 text-sm">
              <p className="font-medium">ສະຫຼຸບ</p>
              <p className="mt-1 text-muted-foreground text-xs">
                ຈະພະຍາຍາມສ້າງ {staffIds.length} ລາຍການ
                {existingDisplayNames.length > 0
                  ? " — ຂ້າມຊື່ທີ່ຊ້ຳໃນ Event/master"
                  : ""}
              </p>
            </div>
          ) : null}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit" disabled={submitting || staffIds.length === 0}>
              {submitting
                ? "ກຳລັງສ້າງ..."
                : `ສ້າງ (${staffIds.length})`}
            </Button>
          </DialogFooter>
        </FormRoot>
      </DialogContent>
    </Dialog>
  );
}
